import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box, Button, IconButton, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { Shift } from '../../interfaces/shift/Shift';
import { ShiftAssignment } from '../../interfaces/shift/ShiftAssignment';
import { ShiftService } from '../../services/shift.service';
import { ShiftAssignmentService } from '../../services/shift-assignment.service';
import { UserService } from '../../services/user.service';
import { displayUsername } from '../../utils/userUtils';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import AssignmentEditDialog from './AssignmentEditDialog';
import BulkAssignmentDialog from './BulkAssignmentDialog';

interface RowUser {
    id: string;
    username: string;
    name?: string;
}

const PAGE_SIZE = 20;

const todayStr = () => toLocalIsoDate();

/** "31-08-2026 → 30-09-2026", or "from 31-08-2026 (open-ended)" when there is no end date. */
export const formatAssignmentRange = (from: string, to?: string | null): string =>
    to ? `${formatDate(from)} → ${formatDate(to)}` : `from ${formatDate(from)} (open-ended)`;

const ShiftAssignmentPage: React.FC = () => {
    const { selectedInstitutionId } = useAuth();
    const toast = useToast();

    const canAssign = usePermission(PERMISSIONS.SHIFT_ASSIGN);
    const canView   = usePermission(PERMISSIONS.SHIFT_VIEW);

    const [asOf, setAsOf] = useState(todayStr());
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const [users, setUsers] = useState<RowUser[]>([]);
    const [shiftsById, setShiftsById] = useState<Map<string, Shift>>(new Map());
    const [assignmentByUser, setAssignmentByUser] = useState<Map<string, ShiftAssignment | null>>(new Map());
    const [loading, setLoading] = useState(true);

    const [editingUser, setEditingUser] = useState<RowUser | null>(null);
    const [bulkOpen, setBulkOpen] = useState(false);

    // Debounce search
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const handleSearchChange = (val: string) => {
        setSearch(val);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            setDebouncedSearch(val);
            setPage(0);
        }, 400);
    };

    // Load shifts once on mount
    useEffect(() => {
        if (!selectedInstitutionId) return;
        ShiftService.list(selectedInstitutionId)
            .then(data => {
                const m = new Map<string, Shift>();
                data.forEach(s => m.set(s.id, s));
                setShiftsById(m);
            })
            .catch(err => toast.showApiError(err, 'Failed to load shifts'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInstitutionId]);

    const fetchData = useCallback(async () => {
        if (!selectedInstitutionId) return;
        setLoading(true);
        try {
            const pageData = await UserService.getInstitutionUsers(page, PAGE_SIZE, selectedInstitutionId, debouncedSearch || undefined, true);
            const content: RowUser[] = (pageData.content || []).map((u: any) => ({
                id: u.id,
                username: u.username,
                name: u.fullName || u.name || undefined,
            }));
            setUsers(content);
            setTotalElements(pageData.totalElements || 0);

            // Fetch active assignment for each visible user
            const entries = await Promise.all(
                content.map(async (u): Promise<[string, ShiftAssignment | null]> => {
                    const asgn = await ShiftAssignmentService.getActive(selectedInstitutionId, u.id, asOf);
                    return [u.id, asgn];
                })
            );
            setAssignmentByUser(new Map(entries));
        } catch (err) {
            toast.showApiError(err, 'Failed to load assignments');
        } finally {
            setLoading(false);
        }
    }, [selectedInstitutionId, page, debouncedSearch, asOf, toast]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleAsOfChange = (val: string) => {
        setAsOf(val);
        setPage(0);
    };

    const handleEditClose = () => setEditingUser(null);
    const handleSaved = () => {
        setEditingUser(null);
        fetchData();
    };
    const handleBulkSaved = () => {
        setBulkOpen(false);
        fetchData();
    };

    const formatRange = (asgn: ShiftAssignment | null | undefined): string => {
        if (!asgn) return '—';
        return formatAssignmentRange(asgn.effectiveFrom, asgn.effectiveTo);
    };

    const shiftCode = (asgn: ShiftAssignment | null | undefined): string => {
        if (!asgn?.shiftId) return '—';
        return shiftsById.get(asgn.shiftId)?.code ?? asgn.shiftId;
    };

    if (!canView) {
        return <Typography sx={{ p: 3 }}>You do not have permission to view shift assignments.</Typography>;
    }

    return (
        <Box data-help-page="shifts.assignments.page" sx={{ padding: 3 }}>
            {/* Toolbar */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Shift Assignments</Typography>
                {canAssign && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setBulkOpen(true)}
                        data-help="shifts.assignments.bulkAssign"
                        data-testid="bulk-assign-btn"
                    >
                        Bulk Assign
                    </Button>
                )}
            </Box>

            {/* Filter bar */}
            <Box data-help-section="shifts.assignments.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
                <TextField
                    data-help="shifts.assignments.asOf"
                    label="As of"
                    type="date"
                    size="small"
                    value={asOf}
                    onChange={e => handleAsOfChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 'data-testid': 'filter-as-of' }}
                    sx={{ flex: '1 1 160px', maxWidth: 200 }}
                />
                <TextField
                    data-help="shifts.assignments.search"
                    label="Search user"
                    size="small"
                    value={search}
                    onChange={e => handleSearchChange(e.target.value)}
                    inputProps={{ 'data-testid': 'filter-search' }}
                    sx={{ flex: '1 1 200px', maxWidth: 280 }}
                />
            </Box>

            {/* Table */}
            {loading ? (
                <Typography>Loading...</Typography>
            ) : (
                <TableContainer data-help-section="shifts.assignments.table" component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="shifts.assignments.col.name">Name</TableCell>
                                <TableCell data-help="shifts.assignments.col.currentShift">Current Shift</TableCell>
                                <TableCell data-help="shifts.assignments.col.range">Effective Range</TableCell>
                                {canAssign && <TableCell data-help="shifts.assignments.col.action">Action</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={canAssign ? 4 : 3}>No users found</TableCell>
                                </TableRow>
                            ) : (
                                users.map(u => {
                                    const asgn = assignmentByUser.get(u.id);
                                    return (
                                        <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                                            <TableCell>
                                                <Typography variant="body2">{u.name || displayUsername(u.username)}</Typography>
                                            </TableCell>
                                            <TableCell>{shiftCode(asgn)}</TableCell>
                                            <TableCell>{formatRange(asgn)}</TableCell>
                                            {canAssign && (
                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        aria-label="edit assignment"
                                                        data-help="shifts.assignments.action.edit"
                                                        onClick={() => setEditingUser(u)}
                                                        data-testid={`edit-btn-${u.id}`}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={totalElements}
                        page={page}
                        onPageChange={(_e, newPage) => setPage(newPage)}
                        rowsPerPage={PAGE_SIZE}
                        rowsPerPageOptions={[PAGE_SIZE]}
                    />
                </TableContainer>
            )}

            {/* Edit dialog */}
            {editingUser && selectedInstitutionId && (
                <AssignmentEditDialog
                    open={!!editingUser}
                    onClose={handleEditClose}
                    userId={editingUser.id}
                    userName={editingUser.name || displayUsername(editingUser.username)}
                    institutionId={selectedInstitutionId}
                    shifts={Array.from(shiftsById.values())}
                    existingAssignment={assignmentByUser.get(editingUser.id) ?? null}
                    onSaved={handleSaved}
                />
            )}

            {/* Bulk dialog */}
            {selectedInstitutionId && (
                <BulkAssignmentDialog
                    open={bulkOpen}
                    onClose={() => setBulkOpen(false)}
                    institutionId={selectedInstitutionId}
                    shifts={Array.from(shiftsById.values())}
                    users={users}
                    onSaved={handleBulkSaved}
                />
            )}
        </Box>
    );
};

export default ShiftAssignmentPage;
